import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, UserCheck, Link2, Clock, Check, X, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessagesModal } from "@/components/MessagesModal";

// Reusable Follow + Connect actions for a business card / profile.
export const NetworkActions = ({ business, onChange, size = "sm" }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState(!!business.is_following);
  const [conn, setConn] = useState(business.connection || null);
  const [msgOpen, setMsgOpen] = useState(false);

  const guard = () => {
    if (!user) { navigate("/login"); return false; }
    return true;
  };

  const handle = async (fn) => {
    if (!guard()) return;
    setBusy(true);
    try { await fn(); onChange && onChange(); }
    catch (e) {
      if (e.response?.status === 403) toast.error("Create your business profile first");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const toggleFollow = () => handle(async () => {
    if (following) { await api.delete(`/follow/${business.id}`); setFollowing(false); }
    else { await api.post(`/follow/${business.id}`); setFollowing(true); toast.success(`Following ${business.business_name}`); }
  });

  const connect = () => handle(async () => {
    const { data } = await api.post(`/connections/request/${business.id}`);
    setConn({ status: "pending", direction: "sent", connection_id: data.connection_id });
    toast.success("Connection request sent");
  });

  const accept = () => handle(async () => {
    await api.post(`/connections/${conn.connection_id}/accept`);
    setConn({ ...conn, status: "accepted" });
    toast.success("Connection accepted");
  });

  const btn = size === "sm" ? "h-9 text-sm" : "h-11";

  return (
    <>
    <div className="flex items-center gap-2 w-full">
      <Button data-testid={`follow-btn-${business.id}`} onClick={toggleFollow} disabled={busy} variant={following ? "outline" : "default"}
        className={`${btn} flex-1 min-w-0 gap-1.5 font-semibold ${following ? "border-slate-300 text-slate-700" : "bg-brand hover:bg-brand-hover"}`}>
        {busy ? <Loader2 className="animate-spin" size={15} /> : following ? <UserCheck size={15} /> : <UserPlus size={15} />}
        {following ? "Following" : "Follow"}
      </Button>

      {conn?.status === "accepted" ? (
        <Button data-testid={`message-btn-${business.id}`} onClick={() => setMsgOpen(true)} className={`${btn} flex-1 min-w-0 gap-1.5 font-semibold bg-emerald-600 hover:bg-emerald-700`}>
          <MessageCircle size={15} /> Message
        </Button>
      ) : conn?.status === "pending" && conn?.direction === "received" ? (
        <Button data-testid={`accept-btn-${business.id}`} onClick={accept} disabled={busy} className={`${btn} flex-1 min-w-0 gap-1.5 font-semibold bg-emerald-600 hover:bg-emerald-700`}>
          <Check size={15} /> Accept
        </Button>
      ) : conn?.status === "pending" ? (
        <Button data-testid={`pending-btn-${business.id}`} disabled variant="outline" className={`${btn} flex-1 min-w-0 gap-1.5 font-semibold border-amber-200 text-amber-700`}>
          <Clock size={15} /> Requested
        </Button>
      ) : (
        <Button data-testid={`connect-btn-${business.id}`} onClick={connect} disabled={busy} variant="outline" className={`${btn} flex-1 min-w-0 gap-1.5 font-semibold border-brand text-brand hover:bg-brand hover:text-white`}>
          <Link2 size={15} /> Connect
        </Button>
      )}
    </div>
    <MessagesModal open={msgOpen} onOpenChange={setMsgOpen} businessId={business.id} businessName={business.business_name} />
    </>
  );
};
